// src/com/dc_veh_ban_state/DcVehBanStateInput.js
import React, { useEffect, useRef, useState } from 'react';
import { Button, DialogContent, DialogActions, Grid, CircularProgress } from '@mui/material';
import DoNotDisturbOnIcon from '@mui/icons-material/DoNotDisturbOn';
import { AppWord, AppObject, AppStyle } from '../../value/AppConst';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import DraggableEditor from '../../common/DraggableEditor';
import VzmTextField from '../../common/VzmTextField';
import VzmCheckbox from '../../common/VzmCheckbox';
import VzmSelector from '../../common/VzmSelector';
import { ErrorHintBox } from '../../common/VzmStyled';
import { useLazyQuery, useReactiveVar } from '@apollo/client';
import InputUtil from '../../value/InputUtil';
import { DcVehBanStateDictionary } from './DcVehBanStateDict';
import { useAlertSetter } from '../../api/app-hook';
import {
    comInfoListRepo, userInfoRepo, vehBanningListRepo, vehInfoListToBanRepo
} from '../../value/AppModel';
import ValueMisc from '../../value/ValueMisc';
import { LIST_COM_INFO } from '../com_info/ComInfoGql';
import { LIST_VEH_INFO_TO_BAN } from '../veh_info/VehInfoGql'; // 금지/금지해제 차량 전체 목록.
import DateRangePicker from '../../common/DateRangePicker';
import AutoTextField from '../../common/AutoTextField';

const InputMap = DcVehBanStateDictionary.inputMap;
const defaultInputData = InputUtil.defaultValuesFromInputMap(InputMap);

/**
 * (DC_VEH_BAN_STATE) 정보 생성 또는 편집 Dialog
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2024-10-02 12:46:44 using DbdbTap
 */
export default function DcVehBanStateInput({
    open,
    item, // item to edit. object of veh_banning view. (not dc_veh_ban_state)
    onClose,
    onRequestSave,
    responseSaving, // Response if result is NOT OK.
}) {
    const sessionInfo = useReactiveVar(userInfoRepo);
    const [input, setInput] = useState({...AppObject.emptyInputData});
    const [isEdit, setIsEdit] = useState(false);
    const comInfoListData = useReactiveVar(comInfoListRepo);
    const [comId, setComId] = useState('');
    const vehBanningListData = useReactiveVar(vehBanningListRepo);
    const vehInfoListToBanData = useReactiveVar(vehInfoListToBanRepo);
    const [vehInfoListOfCompanyNotBanned, setVehInfoListOfCompanyNotBanned] = useState([]);
    const [endDate, setEndDate] = useState(''); // for duration.
    const openConfirmDialog = useConfirmDialog();
    const addAlert = useAlertSetter();
    const bannedVehMapRef = useRef({});
    const {loading:loadingSave, error:errorSave, data:resultSave} = responseSaving; // 전송 결과에 대한 View 기능 소화.

    const [getComInfoList, responseComList] = useLazyQuery(LIST_COM_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetComList(data, option)},
		onError: (error) => addAlert({title: '운송사목록 조회 오류', message: [error.message]})
    });

    const [getVehListToBan, responseNotBannedVehList] = useLazyQuery(LIST_VEH_INFO_TO_BAN, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleateGetVehListToBan(data, option)},
        onError: (error) => addAlert({title: '차량목록 조회 오류', message: [error.message]})
    });

    const getComInfoListOnCondition = () => {
        const now = new Date().getTime() / 1000;
        if(now - comInfoListData.when < 600 && comInfoListData.records.length > 0) return;
        getComInfoList();
    };

    const getNotBanVehListOnCondition = () => {
        const now = new Date().getTime() / 1000;
        if(now - vehInfoListToBanData.when < 600 && vehInfoListToBanData.records.length > 0) return;
        const param = {comId:null};
        getVehListToBan({variables:param});
    };

    // const dcTypeCdOptions = ValueMisc.makeCodesToSelectList(codeTree, CodeGroup.DC_TYPE_CD, InputMap.dcTypeCd.id);

    useEffect(()=>{
        if(open) {
            const inputSet = {...AppObject.emptyInputData};
            let editing = false;
            if(item) {
                inputSet.data = {...item};
                setEndDate(item.vehBanEndTime);
                editing = true;
            }
            else {
                inputSet.data = {...defaultInputData};
                const ymd = ValueMisc.getYMD()
                inputSet.data.vehBanStartTime = ymd;
                setEndDate(ymd);
                inputSet.data.vehBanDays = 1;
            }
            setInput(inputSet);
            setIsEdit(editing);
            getComInfoListOnCondition();
            getNotBanVehListOnCondition();
        }
    }, [open, item]);

    useEffect(()=>{
        if(open) {
            const map = {};
            vehBanningListData.records.forEach((rec)=>{
                map[rec.vehInfoSeq] = true;
            });
            bannedVehMapRef.current = map;
        }
    }, [open, vehBanningListData]);

    useEffect(()=>{
        if(open) {
            const companies = comInfoListData.records.filter((rec)=>rec.dcCd === sessionInfo.user.dscIdAs);
            if(companies.length >0) {
                setComId(companies[0].comId);
            }
        }
    }, [open, comInfoListData, sessionInfo]);

    useEffect(()=>{
        if(open) {
            const notBanned = vehInfoListToBanData.records.filter(
                (rec)=>!bannedVehMapRef.current[rec.vehInfoSeq] && rec.comId === comId
            );
            setVehInfoListOfCompanyNotBanned(notBanned);
        }
    }, [open, vehInfoListToBanData, comId]);

    const onCompleteGetComList = (data, clientOption) => {
        if(data.comInfoList) {
            comInfoListRepo({when:new Date().getTime()/1000, records:data.comInfoList});
        }
    };

    const onCompleateGetVehListToBan = (data, clientOption) => {
        if(data.vehInfoListToBan) {
            vehInfoListToBanRepo({when:new Date().getTime()/1000, records:data.vehInfoListToBan});
        }
    };

    const onChangeValue = (id, value, error) => {
        const newInput = InputUtil.reflectData(input, id, value, error, InputMap, isEdit);
        setInput(newInput);
    };

    const onChangeComId = (id, value) => {
        setComId(value);
        onChangeValue(InputMap.vehInfoSeq.id, null, true);
    };

    const onChangeFrom = (value) => {
        const days = ValueMisc.getDays(value, endDate)+1;
        const input_a = InputUtil.reflectData(input, InputMap.vehBanStartTime.id, value, false, InputMap, isEdit);
        const input_b = InputUtil.reflectData(input_a, InputMap.vehBanDays.id, days, days < 1, InputMap, isEdit);
        setInput(input_b);
    };

    const onChangeTo = (value) => {
        setEndDate(value);
        const days = ValueMisc.getDays(input.data.vehBanStartTime, value)+1;
        onChangeValue(InputMap.vehBanDays.id, days, days < 1);
    };

    const onClickSubmit = () => {
        const param = InputUtil.getDataToSubmit(input.data, InputMap, isEdit);
        onRequestSave(param, isEdit);
    };

    const askCancel = async () => {
        const confirm = await openConfirmDialog({
            ...AppObject.messageCancelEdit,
            option: {yesLabel: AppWord.IgnoreAndClose}
        });
        if(confirm) onClose();
    };

    const onClickCancel = () => {
        if(input.changed) askCancel();
        else onClose();
    };

    const loadingAny = loadingSave || responseComList.loading || responseNotBannedVehList.loading;

    // Input field initiallized using getInputField(c,t,i)
    return(
        <DraggableEditor open={open} maxWidth='md'
            onClose={onClickCancel}
            title={isEdit ? '배차금지정보 수정' : '배차금지정보 등록'}
        >
            <DialogContent>
                <Grid container spacing={2}>
                    {
                        isEdit ?
                        <Grid item xs={12}>
                            <VzmTextField
                                dict={InputMap.vehInfoSeq}
                                value={open && item ? item.vehPlates : ''}
                                disabled={true}
                                fullWidth={true}
                            />
                        </Grid>
                        :
                        <>
                        <Grid item xs={12}>
                            <VzmSelector
                                dict={{
                                    id: 'comId', label: '운송사', labelKey: 'comName',
                                }}
                                value={comId}
                                onChange={onChangeComId}
                                fullWidth={true}
                                list={comInfoListData.records.filter((rec)=>rec.dcCd === sessionInfo.user.dscIdAs)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <AutoTextField
                                dict={InputMap.vehInfoSeq}
                                value={input.data.vehInfoSeq}
                                onChange={onChangeValue}
                                disabled={!Boolean(comId)}
                                fullWidth={true}
                                list={vehInfoListOfCompanyNotBanned}
                            />
                        </Grid>
                        </>
                    }
                    <Grid item xs={12}>
                        <VzmCheckbox
                            dict={InputMap.vehBanLastingFlag}
                            value={input.data.vehBanLastingFlag}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <DateRangePicker
                            dateFrom={input.data.vehBanStartTime}
                            dateTo={endDate}
                            onChangeFrom={onChangeFrom}
                            onChangeTo={onChangeTo}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <div style={{display:'flex', alignItems:'center'}}>
                        <DoNotDisturbOnIcon color={AppStyle.mui.warning} />
                        {
                            input.data.vehBanDays > 0
                            ?
                            <span>{input.data.vehBanDays}일간 금지</span>
                            :
                            <span style={{color:'red'}}>시작날짜가 종료날짜보다 늦습니다.</span>
                        }
                        </div>
                    </Grid>
                
                </Grid>
            </DialogContent>
            <DialogActions style={{display:'flex'}}>
                {
                    input.errorLabels && input.errorLabels.length > 0
                    ?
                    <ErrorHintBox>
                        확인: {input.errorLabels.join(', ')}
                    </ErrorHintBox>
                    : null
                }
                {
                    loadingSave
                    ?
                    <span>
                        <CircularProgress />
                        &nbsp;데이터 저장 중...
                    </span>
                    :
                    <>
                        <Button
                            onClick={onClickSubmit}
                            disabled={input.hasError || !input.changed || loadingAny}
                            variant={AppStyle.mui.contained}
                            color={AppStyle.mui.primary}
                        >저장</Button>
                        <Button onClick={onClickCancel}
                            color={AppStyle.mui.warning}
                            variant={AppStyle.mui.contained}
                        >취소</Button>
                    </>
                }
            </DialogActions>
        </DraggableEditor>
    );
}
